import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DynamicCrudPage from '../../components/DynamicCrudPage';
import { useUsers, useAuth, SCREENS } from '../../hooks';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UserProfileViewModal from '../../components/UserProfileViewModal';

const UsersPage = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserName, setSelectedUserName] = useState(null);

  // Custom form fields with role-based logic
  const customFormFields = useCallback((fields, additionalData, isRootUser, t) => {
    const { users } = additionalData;
    const companies = users?.companies || [];
    const titles = users?.titles || [];
    const teams = users?.teams || [];

    const getAllowedRoles = (userRole) => {
      const hierarchy = {
        ROOT: ['ROOT', 'DIRECTOR', 'GROUP_MANAGER', 'TEAM_MANAGER', 'TEAM_MEMBER'],
        DIRECTOR: ['DIRECTOR', 'GROUP_MANAGER', 'TEAM_MANAGER', 'TEAM_MEMBER'],
        GROUP_MANAGER: ['GROUP_MANAGER', 'TEAM_MANAGER', 'TEAM_MEMBER'],
        TEAM_MANAGER: ['TEAM_MANAGER', 'TEAM_MEMBER'],
        TEAM_MEMBER: ['TEAM_MEMBER'],
      };
      return hierarchy[userRole] || ['TEAM_MEMBER'];
    };

    return fields
      .map(field => {
        if (field.name === 'companyId') {
          return {
            ...field,
            options: companies.map(c => ({ value: c.id, label: c.name })),
            showIf: () => isRootUser,
            required: isRootUser,
            placeholder: isRootUser ? 'form.placeholders.selectCompanyRequired' : field.placeholder,
          };
        }
        if (field.name === 'titleId') {
          return {
            ...field,
            options: titles.map(t => ({ value: t.id, label: t.title_name })),
          };
        }
        if (field.name === 'teamId') {
          return {
            ...field,
            options: teams.map(team => ({ value: team.id, label: team.name })),
          };
        }
        if (field.name === 'role') {
          const allowedRoles = getAllowedRoles(currentUser?.role);
          return {
            ...field,
            options: field.options.filter(opt => allowedRoles.includes(opt.value)),
          };
        }
        if (field.name === 'canViewCommunicationReport') {
          // DynamicForm `showIf`'i sadece formData ile çağırıyor, currentUser
          // ikinci arg olarak geçmiyor → formConfig'deki (_, currentUser)
          // pattern'i undefined döner ve field hep gizlenir. Closure ile
          // currentUser'ı capture edip doğru sonucu döneriz. Hasura
          // update_permissions sunucu tarafında da kısıtlamayı zorlar.
          const canManage =
            currentUser?.role === 'ROOT' || currentUser?.role === 'DIRECTOR';
          return { ...field, showIf: () => canManage };
        }
        return field;
      })
      .filter(field => (field.name === 'companyId' ? isRootUser : true));
  }, [currentUser?.role]);

  // Custom row actions with permission check
  const customRowActions = useCallback((row) => {
    const roleHierarchy = {
      ROOT: 5,
      DIRECTOR: 4,
      GROUP_MANAGER: 3,
      TEAM_MANAGER: 2,
      TEAM_MEMBER: 1,
    };
    
    const currentUserLevel = roleHierarchy[currentUser?.role] || 0;
    const targetUserLevel = roleHierarchy[row?.role] || 0;
    
    const actions = [];
    
    if (currentUser?.role === 'ROOT') {
      actions.push({
        type: 'viewProfile',
        label: 'users.actions.viewProfile',
        color: 'info',
        icon: <VisibilityIcon fontSize="small" />,
      });
    }
    
    if (currentUser?.id !== row?.id && currentUserLevel >= targetUserLevel) {
      actions.push(
        { type: 'edit', label: 'table.actions.edit', color: 'primary' },
        { type: 'delete', label: 'table.actions.delete', color: 'error' }
      );
    }
    
    return actions;
  }, [currentUser, t]);

  const handleRowAction = useCallback((actionType, row) => {
    if (actionType === 'viewProfile') {
      setSelectedUserId(row.id);
      setSelectedUserName(`${row.firstName || row.first_name || ''} ${row.lastName || row.last_name || ''}`.trim());
      setProfileModalOpen(true);
      return true; 
    }
    return false;
  }, []);

  return (
    <>
      <DynamicCrudPage
        entityType="users"
        screen={SCREENS.USERS}
        hook={useUsers}
        additionalHooks={{ users: useUsers }}
        customFormFields={customFormFields}
        customRowActions={customRowActions}
        onRowAction={handleRowAction}
        addButtonTextKey="table.actions.addUser"
        addButtonIcon={<AddIcon />}
        entityTypeForDataTable="user"
        showAddButton={true}
      />
      <UserProfileViewModal
        open={profileModalOpen}
        onClose={() => {
          setProfileModalOpen(false);
          setSelectedUserId(null);
          setSelectedUserName(null);
        }}
        userId={selectedUserId}
        userName={selectedUserName}
      />
    </>
  );
};

export default UsersPage;
